import React, { useState, useRef, useEffect } from 'react';
import HeroRotator from './HeroRotator';

const MAIN_APP = process.env.REACT_APP_API_URL || '';
const PUBLIC_SITE = process.env.REACT_APP_PUBLIC_SITE_ORIGIN || 'https://www.ehandshake.com';

/* ─── Reusable pieces ─── */

function SectionHeading({ eyebrow, title, subtitle }) {
  return (
    <div className="text-center max-w-3xl mx-auto mb-6">
      {eyebrow && (
        <span className="inline-block text-[#00aef7] text-[11px] font-bold uppercase tracking-widest mb-1">
          {eyebrow}
        </span>
      )}
      <h2 className="text-xl md:text-2xl font-bold text-gray-900 leading-tight">{title}</h2>
      {subtitle && <p className="mt-2 text-gray-500 text-sm md:text-base leading-relaxed">{subtitle}</p>}
    </div>
  );
}

function FeatureCard({ icon, title, description }) {
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
      <div className="w-10 h-10 rounded-lg bg-[#00aef7]/10 flex items-center justify-center text-[#00aef7] mb-3 mx-auto">
        {icon}
      </div>
      <h3 className="text-sm font-semibold text-gray-900 mb-1">{title}</h3>
      <p className="text-xs text-gray-500 leading-relaxed">{description}</p>
    </div>
  );
}

function StepCard({ number, title, description }) {
  return (
    <div className="flex gap-3">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#00aef7] text-white flex items-center justify-center font-bold text-xs">
        {number}
      </div>
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-0.5">{title}</h3>
        <p className="text-xs text-gray-500 leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

function ScreenshotPlaceholder({ label }) {
  return (
    <div className="bg-gray-100 rounded-xl border-2 border-dashed border-gray-300 aspect-video flex items-center justify-center">
      <div className="text-center">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" className="w-8 h-8 text-gray-300 mx-auto mb-1">
          <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z" />
        </svg>
        <span className="text-[10px] text-gray-400 font-medium">{label}</span>
      </div>
    </div>
  );
}

function PlatformRow({ icon, bgColor, name, desc }) {
  return (
    <div className="flex gap-3 items-start">
      <div className={`w-8 h-8 rounded-lg ${bgColor} flex items-center justify-center flex-shrink-0`}>
        {icon}
      </div>
      <div>
        <h3 className="text-xs font-semibold text-gray-900">{name}</h3>
        <p className="text-[11px] text-gray-500">{desc}</p>
      </div>
    </div>
  );
}

/* ─── Design Center showcase videos served from CloudFront
 * (behavior /videos/* → S3 bucket ehandshake-public-assets/videos/designcenter/).
 * Override via REACT_APP_DESIGN_VIDEO_BASE_URL if the CDN ever moves. ── */
const DESIGN_VIDEO_BASE_URL =
  process.env.REACT_APP_DESIGN_VIDEO_BASE_URL ||
  'https://www.ehandshake.com/videos/designcenter';

/* ─── Design Center showcase steps (4 chapters: design → preview → landing → publish)
 * A 5th "sharing center" segment plays at the tail of the video but isn't a
 * card here — it acts as the visual handoff to the next homepage section.
 * Each step has a `videoFile` once its clip is cut + uploaded to S3; steps
 * without one fall back to the play-button placeholder. ── */
const DESIGN_CENTER_STEPS = [
  {
    key: 'design',
    label: 'AI-powered Design Center',
    title: 'Design your creation',
    description: 'AI suggests headlines, captions, and visuals tuned to your profession.',
    videoFile: 'DesignCenter1.mp4',
    benefits: [
      '8 idea categories to get your creative juices flowing.',
      'Design once — we format it natively for Facebook, Instagram, X, YouTube, LinkedIn, and TikTok.',
      'Fully describe your idea and let the eHandshake AI design it for you.',
      'Or tap "Inspirations" for fresh AI-generated ideas to post.',
      'Answer a few quick questions — your headline, per-platform captions, hashtags, and emoji are generated automatically.',
      '3.2 million royalty-free stock images and videos at your fingertips.',
      'Visuals tuned to your profession are surfaced first.',
      'Or upload your own photo or video.',
    ],
  },
  {
    key: 'preview',
    label: 'Preview Social Posts',
    title: 'Preview every platform',
    description: 'See how it lands on Facebook, Instagram, X, LinkedIn, TikTok, and YouTube — native format each.',
    videoFile: 'DesignCenter2.mp4',
    benefits: [
      'Fine-tune headline position on each platform for the best fit.',
      'Customize captions, hashtags, and emoji per platform so each post feels native.',
      'Turn static images into short videos for YouTube and TikTok posts.',
      'When every platform looks right, continue on to build the landing page your social links click through to.',
    ],
  },
  {
    key: 'landing',
    label: 'Create Landing Page',
    title: 'Create a landing page',
    description: 'A full mini-site for the creation — AI writes the copy, you approve.',
    videoFile: 'DesignCenter3.mp4',
    benefits: [
      'Link your own landing page, or let our AI craft a fully customizable one for you.',
      'Link out to any public page — YouTube, Facebook, Pinterest, your online store, or a blog on your own website.',
      'Drop in more stock images and videos, or upload your own.',
      'Choose from 4 pre-designed templates: Bold Hero, Grid Gallery, Feature Strip, or Compact Editorial.',
      'Match the accent color to your brand.',
      'Edit any AI-generated text, add as many sections as you want, and wire up call-to-action links.',
    ],
  },
  {
    key: 'publish',
    label: 'Publish Your Creation',
    title: 'Publish to the sharing center',
    description: 'Native-format posts go live on every platform you\'ve connected.',
    videoFile: 'SharingCenter.mp4',
    benefits: [
      'Publish one creation to every platform bucket — already formatted for each.',
      'See the social post and its landing page side-by-side, per platform.',
      'Browse other users\' posts per platform — one-click copy-link or jump straight to the live post.',
      'Publish to Facebook, Instagram, X, YouTube, LinkedIn, TikTok, and email — all from one place.',
      'Track likes, comments, shares, and views per platform.',
      'Recommend and share a teammate\'s profile, company profile, or any team profile.',
    ],
  },
];

const videoUrlForStep = (step) =>
  step?.videoFile ? `${DESIGN_VIDEO_BASE_URL}/${step.videoFile}` : null;

/* ─── Design Center Showcase ───────────────────────────────────────────────
 * Apple-style layout: info text (left col) + cards row above video (right col).
 * Behavior:
 *   - activeIdx  = last-clicked card ("pinned"). Starts at 0.
 *   - hoverIdx   = currently hovered card. null when not hovering.
 *   - displayIdx = hoverIdx ?? activeIdx. Drives left-column text AND the
 *                  video-area thumbnail preview (before first play).
 *   - hasStarted = false on first load. Play button overlay shown until the
 *                  user clicks play OR any card.
 *   - After hasStarted=true, card clicks immediately swap to that segment.
 *     Hover while playing updates ONLY the left-column text; the video keeps
 *     playing its current segment.
 * ────────────────────────────────────────────────────────────────────────── */
function DesignCenterShowcase() {
  const [activeIdx, setActiveIdx] = useState(0);
  const [hoverIdx,  setHoverIdx]  = useState(null);
  const [hasStarted, setHasStarted] = useState(false);
  const [isEnded,   setIsEnded]   = useState(false);
  const videoRef = useRef(null);

  // Left column follows the hover if present, else the pinned (active) step.
  const displayIdx = hoverIdx !== null ? hoverIdx : activeIdx;

  // Thumbnail shown in the video area when nothing is playing.
  // Once playback has started, the stage is dominated by the active segment
  // regardless of what's hovered (hover only affects the left-column text).
  const thumbnailIdx = hasStarted ? activeIdx : displayIdx;

  const handleCardClick = (i) => {
    setActiveIdx(i);
    setHasStarted(true);
    setIsEnded(false);
  };

  const handleReplay = () => {
    const el = videoRef.current;
    if (!el) return;
    setIsEnded(false);
    el.currentTime = 0;
    el.play().catch(() => {});
  };

  const displayStep   = DESIGN_CENTER_STEPS[displayIdx];
  const thumbnailStep = DESIGN_CENTER_STEPS[thumbnailIdx];
  const videoUrl      = videoUrlForStep(thumbnailStep);

  // Sync the <video> element to the current segment. Before hasStarted, setting
  // src shows the first-frame thumbnail (preload="metadata"). Once hasStarted
  // is true or activeIdx changes, play from the start of the new segment.
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !videoUrl) return;
    // Avoid clobbering the stream if the src is already correct.
    const current = el.src.split('?')[0];
    if (current !== videoUrl) {
      el.src = videoUrl;
      el.load();
    }
    if (hasStarted) {
      el.currentTime = 0;
      setIsEnded(false);
      el.play().catch(() => {});
    }
  }, [videoUrl, hasStarted]);

  return (
    <section className="bg-gray-50 border-y border-gray-100">
      <div className="mx-auto w-[90%] max-w-6xl py-10 md:py-14">

        {/* Section header — brand-blue hero title above the 2×2 grid. */}
        <h2 className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-[#00aef7] tracking-tight text-center mb-10 md:mb-14">
          AI-powered Design & Sharing Center
        </h2>

        {/* 2×2 grid. Row 1: left heading + arrow / right cards.
            Row 2: left step info (hover-driven) / right video.
            Columns line up so the heading sits beside the cards and the
            hover-driven info sits beside the video. Stacks on mobile. */}
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_820px] md:grid-rows-[auto_auto] gap-x-8 md:gap-x-10 gap-y-3 md:gap-y-4">

          {/* ─── ROW 1 / LEFT: "Easy 4 step process" heading + arrow ─── */}
          <div className="flex flex-col justify-center">
            <h2 className="text-2xl md:text-3xl font-extrabold text-[#00aef7] leading-[1.1] tracking-tight flex items-center gap-2 md:gap-3">
              <span className="whitespace-nowrap">Easy 4 step process</span>
              {/* Horizontal arrow pointing at the cards on the right. Hidden on
                  mobile since the cards stack below, not beside. */}
              <svg
                className="hidden md:block w-10 h-4 flex-shrink-0"
                viewBox="0 0 100 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M2 10 h 86 M80 2 l 10 8 l -10 8"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              </svg>
            </h2>
            <p className="mt-2 text-gray-700 text-sm md:text-base leading-relaxed max-w-md">
              Design a creation and landing page in under 5 minutes with the help of our AI assistant.
            </p>
          </div>

          {/* ─── ROW 1 / RIGHT: 4 minimal step cards ─── */}
          <div className="grid grid-cols-4 gap-2">
            {DESIGN_CENTER_STEPS.map((step, i) => {
              const isActive = i === activeIdx;
              const isHover  = i === hoverIdx;
              return (
                <button
                  key={step.key}
                  type="button"
                  onMouseEnter={() => setHoverIdx(i)}
                  onMouseLeave={() => setHoverIdx(null)}
                  onClick={() => handleCardClick(i)}
                  className={`px-1 py-2 rounded-lg border-2 bg-white transition-all flex flex-col items-center justify-center gap-1 ${
                    isActive
                      ? 'border-[#00aef7] shadow-md'
                      : isHover
                        ? 'border-gray-400 shadow-sm'
                        : 'border-gray-200'
                  }`}
                >
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${
                      isActive ? 'bg-[#00aef7] text-white' : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {i + 1}
                  </div>
                  <div
                    className={`text-[10px] uppercase tracking-wider font-bold text-center leading-tight ${
                      isActive ? 'text-[#00aef7]' : 'text-gray-500'
                    }`}
                  >
                    {step.label}
                  </div>
                </button>
              );
            })}
          </div>

          {/* ─── ROW 2 / LEFT: step-dependent content.
              If the current step has a `benefits` array, show brand-blue-check
              bullets. Otherwise fall back to the original hover-driven title +
              description block. ─── */}
          <div className="flex items-start min-h-[160px] md:min-h-[360px]">
            {displayStep.benefits ? (
              <ul className="space-y-2.5 md:space-y-3 w-full max-w-md">
                {displayStep.benefits.map((text, i) => (
                  <li key={i} className="flex gap-3 items-start">
                    <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-[#00aef7] text-white flex items-center justify-center shadow-sm">
                      <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M16.704 5.29a1 1 0 010 1.42l-7.997 8a1 1 0 01-1.414 0l-3.997-4a1 1 0 011.414-1.42L8 12.58l7.29-7.29a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </span>
                    <span className="text-gray-700 text-xs md:text-sm leading-snug">
                      {text}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-center max-w-sm px-2 mx-auto">
                <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#00aef7] mb-2">
                  Step {displayIdx + 1}
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight mb-3">
                  {displayStep.title}
                </h3>
                <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                  {displayStep.description}
                </p>
              </div>
            )}
          </div>

          {/* ─── ROW 2 / RIGHT: video stage ─── */}
          <div>
            <div
              className="relative rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/10 bg-gradient-to-br from-gray-900 via-gray-800 to-black"
              style={{ aspectRatio: '1280 / 608' }}
            >
              {/* Real <video> when this segment has a file uploaded. Before
                  hasStarted, preload="metadata" shows the first frame as
                  thumbnail. Rollovers change thumbnailIdx → useEffect swaps
                  src. Clicking a card or the play button sets hasStarted and
                  the effect calls play(). */}
              {videoUrl && (
                <video
                  ref={videoRef}
                  muted
                  playsInline
                  preload="metadata"
                  controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
                  disablePictureInPicture
                  disableRemotePlayback
                  onContextMenu={(e) => e.preventDefault()}
                  onEnded={() => setIsEnded(true)}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              )}

              {/* Replay overlay — visible when the current segment has ended.
                  Clear signal that playback is done + one-tap to watch again,
                  since there's no progress bar and autoplay doesn't loop. */}
              {hasStarted && videoUrl && isEnded && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/55 backdrop-blur-sm transition-opacity">
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={handleReplay}
                      className="group w-16 h-16 md:w-20 md:h-20 rounded-full bg-[#00aef7] hover:bg-[#0098d8] shadow-xl flex items-center justify-center mx-auto mb-3 transition-all hover:scale-105"
                      aria-label="Replay this segment"
                      title="Replay"
                    >
                      <svg className="w-8 h-8 md:w-10 md:h-10 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 12a9 9 0 1 0 3-6.7" />
                        <polyline points="3 4 3 10 9 10" />
                      </svg>
                    </button>
                    <div className="text-white text-xs md:text-sm font-medium mt-1">
                      Segment complete &mdash; click a card for the next step
                    </div>
                  </div>
                </div>
              )}

              {/* Dark scrim over the first frame until the user hits play. The
                  video still shows behind it (you can faintly see the source
                  frame), but the scrim dims it enough that the play button
                  has real contrast instead of fighting with blurry UI text. */}
              <div
                className={`absolute inset-0 bg-gradient-to-br from-black/70 via-black/75 to-black/70 transition-opacity duration-500 pointer-events-none ${
                  hasStarted && videoUrl ? 'opacity-0' : 'opacity-100'
                }`}
              />

              {/* Play button + label overlay. Shown before hasStarted, also
                  shown for segments with no video uploaded yet. Hidden only
                  when the video is actually playing a real file. */}
              <div
                className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
                  hasStarted && videoUrl ? 'opacity-0 pointer-events-none' : 'opacity-100'
                }`}
              >
                <div className="text-center">
                  {!hasStarted && (
                    <button
                      type="button"
                      onClick={() => handleCardClick(thumbnailIdx)}
                      className="group w-16 h-16 md:w-20 md:h-20 rounded-full bg-[#00aef7] hover:bg-[#0098d8] shadow-xl flex items-center justify-center mx-auto mb-3 transition-all hover:scale-105"
                      aria-label="Play walkthrough"
                      disabled={!videoUrl}
                    >
                      <svg className="w-7 h-7 md:w-9 md:h-9 text-white ml-1" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </button>
                  )}
                  <div className="text-white text-xs md:text-sm font-medium mt-1">
                    {thumbnailStep.label}
                  </div>
                  {!videoUrl && (
                    <div className="text-white/50 text-[10px] mt-1">
                      Video coming soon
                    </div>
                  )}
                </div>
              </div>

              {/* Fullscreen toggle — visible once the video is playing. Uses the
                  browser's native requestFullscreen so the user can expand the
                  walkthrough to the full display when they want a better look. */}
              {hasStarted && videoUrl && (
                <button
                  type="button"
                  onClick={() => {
                    const el = videoRef.current;
                    if (!el) return;
                    const req = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen;
                    if (req) req.call(el).catch(() => {});
                  }}
                  className="absolute bottom-3 right-3 w-9 h-9 rounded-lg bg-black/50 hover:bg-black/75 backdrop-blur-sm flex items-center justify-center text-white transition opacity-80 hover:opacity-100"
                  aria-label="Expand to fullscreen"
                  title="Fullscreen"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" />
                  </svg>
                </button>
              )}

            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Page ─── */

const Home = () => {
  return (
    <main className="flex-grow bg-white">

      {/* ══════════ HERO ══════════ */}
      <HeroRotator />


      {/* ══════════ EMPATHY STORY — 9 PM vignette ══════════ */}
      <section className="bg-[#faf7f2] border-y border-stone-200">
        <div className="mx-auto w-[90%] max-w-3xl py-14 md:py-20">
          <div className="text-center mb-10">
            <span className="inline-block text-[#00aef7] text-[11px] font-bold uppercase tracking-widest mb-3">
              The Problem Every Small Business Owner Knows
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
              It&rsquo;s 9 PM.
            </h2>
          </div>

          <div className="font-serif text-gray-700 text-base md:text-lg leading-relaxed space-y-5">
            <p>
              The lights are off in the shop. You sit down with a cup of coffee
              and decide: tonight, I&rsquo;m going to finally post something on
              social media.
            </p>
            <p>
              You open Instagram. <em>What do I say?</em> You open Facebook.{' '}
              <em>What photo do I use?</em> You try Canva. You freeze. You google{' '}
              &ldquo;how to post as a plumber&rdquo; and get overwhelmed. An
              hour later, you close the laptop and go to bed.
            </p>
            <p>
              Tomorrow you&rsquo;ll be on a job site at 7 AM. Then another. Then
              another. Marketing will wait.
            </p>
          </div>

          {/* Photo storyboard — 9 PM → 10 PM → 11 PM → 7 AM */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
            {[
              { time: '9 PM',  label: 'Sit down with good intentions', src: '/images/empathy/empathy-9pm-coffee.jpg',   alt: 'Coffee mug on a home kitchen table in the evening, laptop nearby' },
              { time: '10 PM', label: 'The idea repository is empty',     src: '/images/empathy/empathy-10pm-stuck.jpg',    alt: 'Face lit by a laptop screen, thinking, frustrated' },
              { time: '11 PM', label: 'Close the laptop. It can wait.',    src: '/images/empathy/empathy-11pm-closing.jpg',  alt: 'Laptop being closed in a dimly lit room late at night' },
              { time: '7 AM',  label: 'Back on the job. Again.',       src: '/images/empathy/empathy-7am-jobsite.jpg',   alt: 'Tradesman starting work at a job site in the early morning' },
            ].map(({ time, label, src, alt }) => (
              <figure key={time} className="text-center">
                <div className="overflow-hidden rounded-lg shadow-md ring-1 ring-stone-200/70">
                  <img
                    src={src}
                    alt={alt}
                    loading="lazy"
                    className="w-full aspect-[3/2] object-cover"
                  />
                </div>
                <figcaption className="mt-3">
                  <div className="text-sm font-semibold text-gray-800 tracking-wide">{time}</div>
                  <div className="text-[11px] md:text-xs text-gray-500 mt-0.5 leading-snug">
                    {label}
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>

          <div className="mt-8 font-serif text-gray-800 text-base md:text-lg leading-relaxed space-y-4">
            <p>
              We built eHandshake because this happens to every single owner or salesperson in
              millions of small businesses.
            </p>
            <p className="font-semibold text-gray-900">
              You&rsquo;re not bad at marketing. You&rsquo;re a specialist at
              what you do, and marketing is its own specialty. You shouldn&rsquo;t
              have to become two professionals.
            </p>
          </div>
        </div>
      </section>


      {/* ══════════ DESIGN CENTER SHOWCASE — interactive cards + video ══════════ */}
      <DesignCenterShowcase />


      {/* ══════════ HOW IT WORKS ══════════ */}
      <section className="bg-gradient-to-br from-[#00aef7] to-[#0088cc] text-white">
        <div className="mx-auto w-[90%] max-w-6xl py-10 md:py-12">
          <div className="text-center max-w-3xl mx-auto mb-8">
            <h2 className="text-xl md:text-2xl font-bold">From Zero to Published in 5 Minutes</h2>
            <p className="mt-2 text-white/80 text-sm">No marketing degree. No design skills. If you can tap a screen, you can market your business.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { step: '1', title: 'Sign Up', desc: 'Create your free account in 30 seconds. Look for the signup email and answer a few questions.' },
              { step: '2', title: 'Pick an Idea', desc: 'Open the Design Center and browse content ideas tailored to your trade.' },
              { step: '3', title: 'We Build It', desc: 'AI generates headlines, captions, and landing page content, text and call-to-action buttons.' },
              { step: '4', title: 'Publish', desc: 'Natively-formatted for each platform. You choose which ones go live and on which platforms.' },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-11 h-11 rounded-full bg-white/20 backdrop-blur flex items-center justify-center mx-auto mb-3">
                  <span className="text-lg font-bold">{item.step}</span>
                </div>
                <h3 className="font-semibold text-sm mb-1">{item.title}</h3>
                <p className="text-xs text-white/75 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* ══════════ BUILT-IN ANALYTICS — dual Facebook + TikTok screenshots.
           Written for Meta / TikTok app-review scanners: the words
           "Facebook", "Instagram", "TikTok", "YouTube", "LinkedIn",
           "X", "analytics", "likes", "comments", "shares", "views"
           all appear together in plain text with a visible screenshot
           proving the data is displayed to the user, not just stored. ══════════ */}
      <section className="bg-gray-50 border-y border-gray-100">
        <div className="mx-auto w-[90%] max-w-6xl py-14 md:py-20">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <span className="inline-block text-[#00aef7] text-[11px] font-bold uppercase tracking-widest mb-3">
              Built-in Analytics
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
              See how every post performs across every platform.
            </h2>
            <p className="mt-4 text-gray-600 text-sm md:text-base leading-relaxed">
              Once you publish, eHandshake reads engagement metrics back from each connected platform &mdash; <strong>likes, comments, shares, and views</strong> &mdash; so you can see exactly how your creations perform on <strong>Facebook, Instagram, X, LinkedIn, TikTok, and YouTube</strong> in one place. No more hopping between six dashboards.
            </p>
          </div>

          {/* Two screenshots side-by-side — Facebook metrics view + TikTok metrics view. */}
          <div className="grid md:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto">
            <figure>
              <div className="rounded-2xl overflow-hidden shadow-xl bg-white">
                <img
                  src="/images/team/analytics-dashboardFB.png"
                  alt="eHandshake analytics view for a Facebook post — likes, comments, shares, and views displayed inline with the creation."
                  className="w-full h-auto block"
                  loading="lazy"
                />
              </div>
              <figcaption className="mt-2 text-center text-xs text-gray-500">
                Facebook engagement, in context with the post.
              </figcaption>
            </figure>

            <figure>
              <div className="rounded-2xl overflow-hidden shadow-xl bg-white">
                <img
                  src="/images/team/analytics-dashboardTT.png"
                  alt="eHandshake analytics view for a TikTok post — views, likes, comments, and shares displayed inline with the creation."
                  className="w-full h-auto block"
                  loading="lazy"
                />
              </div>
              <figcaption className="mt-2 text-center text-xs text-gray-500">
                TikTok views &amp; engagement, same view, same place.
              </figcaption>
            </figure>
          </div>

          {/* Data-use note — preempts the "what do you do with the data?"
              question that always comes up in Meta / TikTok review. */}
          <p className="mt-10 max-w-2xl mx-auto text-center text-xs md:text-sm text-gray-500 leading-relaxed">
            We only read the engagement data you&rsquo;ve authorized for creations you publish through eHandshake. Your audience&rsquo;s private information is never accessed or stored.
          </p>
        </div>
      </section>


      {/* ══════════ TEAMS LEAD-IN — "you published. now what?" ══════════ */}
      <section className="bg-[#faf7f2] border-y border-stone-200">
        <div className="mx-auto w-[90%] max-w-3xl py-14 md:py-20">
          <div className="text-center mb-8">
            <span className="inline-block text-[#00aef7] text-[11px] font-bold uppercase tracking-widest mb-3">
              You Published. Now What?
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
              Congratulations. Your creation is ready to post on all social platforms.
            </h2>
          </div>

          <div className="font-serif text-gray-700 text-base md:text-lg leading-relaxed space-y-5">
            <p>
              But here&rsquo;s the quieter question: <em>who&rsquo;s actually going to see it after you post it?</em>
            </p>
            <p>
              Only the followers you already have. The same audience you had
              yesterday, last month, last year. And every platform&rsquo;s algorithm
              decides whether even <em>they</em> see it in their feed.
            </p>
            <p>
              To grow, you need new eyes. More people encountering your name,
              your face, your business &mdash; an audience that didn&rsquo;t exist
              for you until today.
            </p>
          </div>

          <div className="mt-8 text-center">
            <p className="font-sans text-xl md:text-2xl font-semibold text-[#00aef7]">
              That&rsquo;s what Teams are for
            </p>
          </div>
        </div>
      </section>


      {/* ══════════ DRIP MARKETING vs DRIP NETWORKING comparison — TEMPORARILY HIDDEN.
           Brett is reworking this section. To re-enable, change `false && (`
           to `true && (` (or just remove the guard entirely) on the wrapper
           below. All markup preserved so nothing is lost. ══════════ */}
      {false && (
      <section className="bg-white">
        <div className="mx-auto w-[90%] max-w-6xl py-14 md:py-20">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="inline-block text-[#00aef7] text-[11px] font-bold uppercase tracking-widest mb-3">
              A New Standard
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 leading-tight">
              Drip Marketing vs. <span className="text-[#00aef7]">Drip Networking<sup className="text-base md:text-lg font-semibold align-super">&trade;</sup></span>
            </h2>
            <p className="mt-5 text-gray-600 text-sm md:text-base leading-relaxed">
              <strong className="text-gray-900">Drip Networking</strong>: the regular posts you&rsquo;re already making, distributed across an entire team of teammates&rsquo; audiences &mdash; so every post compounds instead of just dripping into your own pond.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8 items-stretch mb-14">

            {/* ─── Column 1: Drip Marketing ─── */}
            <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6 md:p-8 flex flex-col">
              <div className="text-center mb-5">
                <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">The Old Way</span>
                <h3 className="mt-1 text-xl md:text-2xl font-bold text-gray-900">Drip Marketing</h3>
              </div>

              {/* Graphic: one business + small ring of reached followers, lots of grey "unreached" dots outside */}
              <div className="flex justify-center mb-6">
                <svg width="280" height="200" viewBox="0 0 280 200" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  {/* Unreached audience — scattered grey dots */}
                  <g fill="#d1d5db">
                    <circle cx="20" cy="25" r="3" />
                    <circle cx="45" cy="15" r="3" />
                    <circle cx="75" cy="22" r="3" />
                    <circle cx="205" cy="18" r="3" />
                    <circle cx="235" cy="25" r="3" />
                    <circle cx="260" cy="40" r="3" />
                    <circle cx="15" cy="65" r="3" />
                    <circle cx="15" cy="130" r="3" />
                    <circle cx="265" cy="110" r="3" />
                    <circle cx="260" cy="140" r="3" />
                    <circle cx="20" cy="175" r="3" />
                    <circle cx="45" cy="185" r="3" />
                    <circle cx="80" cy="180" r="3" />
                    <circle cx="200" cy="180" r="3" />
                    <circle cx="230" cy="180" r="3" />
                    <circle cx="255" cy="170" r="3" />
                    <circle cx="105" cy="15" r="3" />
                    <circle cx="170" cy="15" r="3" />
                    <circle cx="130" cy="185" r="3" />
                    <circle cx="150" cy="185" r="3" />
                  </g>

                  {/* Reach ripples (soft, fading outward) */}
                  <circle cx="140" cy="100" r="72" fill="none" stroke="#00aef7" strokeWidth="1" opacity="0.15" />
                  <circle cx="140" cy="100" r="58" fill="none" stroke="#00aef7" strokeWidth="1" opacity="0.3" />
                  <circle cx="140" cy="100" r="44" fill="none" stroke="#00aef7" strokeWidth="1" opacity="0.5" />

                  {/* Followers reached — small blue dots inside the rings */}
                  <g fill="#7dd3fc">
                    <circle cx="175" cy="100" r="4" />
                    <circle cx="165" cy="130" r="4" />
                    <circle cx="140" cy="140" r="4" />
                    <circle cx="115" cy="130" r="4" />
                    <circle cx="105" cy="100" r="4" />
                    <circle cx="115" cy="70" r="4" />
                    <circle cx="140" cy="60" r="4" />
                    <circle cx="165" cy="70" r="4" />
                  </g>

                  {/* Business node */}
                  <circle cx="140" cy="100" r="14" fill="#00aef7" opacity="0.2" />
                  <circle cx="140" cy="100" r="10" fill="#00aef7" />
                </svg>
              </div>

              <p className="text-gray-800 text-sm md:text-base font-semibold mb-2">
                One business. One post. One network.
              </p>
              <p className="text-gray-600 text-sm md:text-[15px] leading-relaxed mb-3">
                You publish. Your existing followers see it &mdash; or the ones the algorithm decides to show. Next week, you publish again. Same audience. Same ceiling.
              </p>
              <p className="text-gray-600 text-sm md:text-[15px] leading-relaxed">
                Your reach is capped at the people who already know you.
              </p>
            </div>

            {/* ─── Column 2: Drip Networking ─── */}
            <div className="bg-[#00aef7]/5 rounded-2xl border-2 border-[#00aef7]/40 p-6 md:p-8 flex flex-col">
              <div className="text-center mb-5">
                <span className="text-[10px] font-bold uppercase tracking-widest text-[#00aef7]">The New Standard</span>
                <h3 className="mt-1 text-xl md:text-2xl font-bold text-gray-900">Drip Networking</h3>
              </div>

              {/* Graphic: 4 team members linked, each with their own follower cluster.
                  Very few "unreached" dots left — cumulative team reach fills the canvas. */}
              <div className="flex justify-center mb-6">
                <svg width="280" height="200" viewBox="0 0 280 200" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  {/* Team connection lines — full mesh between 4 businesses */}
                  <g stroke="#00aef7" strokeWidth="1.5" opacity="0.6" fill="none">
                    <line x1="65" y1="55" x2="215" y2="55" />
                    <line x1="215" y1="55" x2="215" y2="145" />
                    <line x1="215" y1="145" x2="65" y2="145" />
                    <line x1="65" y1="145" x2="65" y2="55" />
                    <line x1="65" y1="55" x2="215" y2="145" />
                    <line x1="215" y1="55" x2="65" y2="145" />
                  </g>

                  {/* Top-left team member: reach ripples + followers */}
                  <circle cx="65" cy="55" r="32" fill="none" stroke="#00aef7" strokeWidth="1" opacity="0.22" />
                  <circle cx="65" cy="55" r="22" fill="none" stroke="#00aef7" strokeWidth="1" opacity="0.4" />
                  <g fill="#7dd3fc">
                    <circle cx="35" cy="40" r="3" />
                    <circle cx="30" cy="65" r="3" />
                    <circle cx="85" cy="30" r="3" />
                    <circle cx="55" cy="25" r="3" />
                  </g>
                  <circle cx="65" cy="55" r="8" fill="#00aef7" />

                  {/* Top-right */}
                  <circle cx="215" cy="55" r="32" fill="none" stroke="#00aef7" strokeWidth="1" opacity="0.22" />
                  <circle cx="215" cy="55" r="22" fill="none" stroke="#00aef7" strokeWidth="1" opacity="0.4" />
                  <g fill="#7dd3fc">
                    <circle cx="245" cy="40" r="3" />
                    <circle cx="250" cy="65" r="3" />
                    <circle cx="195" cy="30" r="3" />
                    <circle cx="225" cy="25" r="3" />
                  </g>
                  <circle cx="215" cy="55" r="8" fill="#00aef7" />

                  {/* Bottom-left */}
                  <circle cx="65" cy="145" r="32" fill="none" stroke="#00aef7" strokeWidth="1" opacity="0.22" />
                  <circle cx="65" cy="145" r="22" fill="none" stroke="#00aef7" strokeWidth="1" opacity="0.4" />
                  <g fill="#7dd3fc">
                    <circle cx="30" cy="140" r="3" />
                    <circle cx="40" cy="170" r="3" />
                    <circle cx="85" cy="170" r="3" />
                    <circle cx="55" cy="180" r="3" />
                  </g>
                  <circle cx="65" cy="145" r="8" fill="#00aef7" />

                  {/* Bottom-right */}
                  <circle cx="215" cy="145" r="32" fill="none" stroke="#00aef7" strokeWidth="1" opacity="0.22" />
                  <circle cx="215" cy="145" r="22" fill="none" stroke="#00aef7" strokeWidth="1" opacity="0.4" />
                  <g fill="#7dd3fc">
                    <circle cx="250" cy="140" r="3" />
                    <circle cx="240" cy="170" r="3" />
                    <circle cx="195" cy="170" r="3" />
                    <circle cx="225" cy="180" r="3" />
                  </g>
                  <circle cx="215" cy="145" r="8" fill="#00aef7" />

                  {/* Center "team" indicator — small ehandshake mark */}
                  <circle cx="140" cy="100" r="14" fill="white" stroke="#00aef7" strokeWidth="2" />
                  <text x="140" y="105" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="14" fontWeight="700" fill="#00aef7">eH</text>

                  {/* A couple of still-unreached dots — far fewer than the marketing graphic */}
                  <g fill="#d1d5db">
                    <circle cx="10" cy="100" r="3" />
                    <circle cx="270" cy="100" r="3" />
                    <circle cx="140" cy="12" r="3" />
                    <circle cx="140" cy="190" r="3" />
                  </g>
                </svg>
              </div>

              <p className="text-gray-800 text-sm md:text-base font-semibold mb-2">
                A team of businesses, each amplifying every other&rsquo;s best work.
              </p>
              <p className="text-gray-600 text-sm md:text-[15px] leading-relaxed mb-3">
                When your teammate posts, you share it. When you post, they share it. Every post drips into every teammate&rsquo;s audience &mdash; people you&rsquo;d never have reached alone.
              </p>
              <p className="text-gray-600 text-sm md:text-[15px] leading-relaxed mb-5">
                Your reach compounds with every teammate who joins. No extra posts. No extra effort.
              </p>

              {/* Math callout — makes the compounding effect concrete */}
              <div className="mt-auto rounded-xl bg-white border border-[#00aef7]/30 p-4">
                <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2 text-center">
                  The Math
                </div>
                <div className="space-y-1.5 text-center">
                  <div className="text-xs md:text-sm text-gray-500">
                    Solo: <span className="font-semibold text-gray-700">500 followers × 1 post = 500 eyes</span>
                  </div>
                  <div className="text-xs md:text-sm text-gray-900">
                    Team of 10: <span className="font-bold text-[#00aef7]">5,000+ eyes</span> &mdash; every post, automatically.
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ─── Team screenshot — real product view of a team (Winter Park Marlins).
              File lives at public/images/team/team-dashboard.png ─── */}
          <div className="mx-auto max-w-[900px]">
            <div className="rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/10 bg-white">
              <img
                src="/images/team/team-dashboard.png"
                alt="eHandshake team dashboard showing the Winter Park Marlins team — captain Sarah McLaughlin, 11 teammates, team score 4.99, connected social channels, and a live activity feed of teammates' recent posts and shares."
                className="w-full h-auto block"
                loading="lazy"
              />
            </div>
            <p className="mt-3 text-center text-xs text-gray-500">
              A real eHandshake team. When anyone on this team posts, it drips through the entire group&rsquo;s combined audience.
            </p>
          </div>
        </div>
      </section>
      )}


      {/* ══════════ TEAMS ══════════ */}
      <section>
        <div className="mx-auto w-[90%] max-w-6xl py-10 md:py-12">
          <SectionHeading
            eyebrow="Grow Together"
            title="Team Up. Cross-Promote. Win."
            subtitle="Join a team of local, small businesses. When one teammate creates a post, everyone can share it, like it and comment on it — multiplying reach without multiplying effort. Don't let the platform algorithms decide who sees your work. Build a human network that does it for you."
          />
          <div className="grid md:grid-cols-3 gap-4">
            <FeatureCard
              icon={<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" /></svg>}
              title="Build Your Team"
              description="Invite complementary local businesses — the roofer, the electrician, the HVAC tech. Support each other's marketing with zero extra work."
            />
            <FeatureCard
              icon={<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z" /></svg>}
              title="Share Each Other's Content"
              description="When your teammate posts a tip about water heaters, you can share it to your audience — and they'll do the same for you."
            />
            <FeatureCard
              icon={<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z" /></svg>}
              title="Grow Collectively"
              description="Your team score combines everyone's reviews. A strong team means more trust, more visibility, and more customers for all."
            />
          </div>

          {/* ─── Real team screenshot — Winter Park Marlins.
              File lives at public/images/team/team-dashboard.png ─── */}
          <div className="mx-auto max-w-[900px] mt-10 md:mt-14">
            <div className="rounded-2xl overflow-hidden shadow-xl bg-white">
              <img
                src="/images/team/team-dashboard.png"
                alt="eHandshake team dashboard showing the Winter Park Marlins team — captain, 11 teammates, team score 4.99, connected social channels, and a live activity feed of teammates' recent posts and shares."
                className="w-full h-auto block"
                loading="lazy"
              />
            </div>
            <p className="mt-3 text-center text-xs text-gray-500">
              A real eHandshake team. When anyone on this team posts, it drips through the entire group&rsquo;s combined audience.
            </p>
          </div>
        </div>
      </section>


      {/* ══════════ TECH PROVIDER ══════════ */}
      <section>
        <div className="mx-auto w-[90%] max-w-6xl py-10 md:py-12">
          <SectionHeading
            eyebrow="Technology Platform"
            title="Built for Businesses. Powered by Integrations."
            subtitle="eHandshake is a social media management platform that connects to Facebook, Instagram, X, YouTube, LinkedIn, and TikTok via official APIs. We publish content on behalf of our users with their explicit authorization through OAuth."
          />
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">How We Access User Data</h3>
              <ul className="space-y-1.5 text-xs text-gray-600">
                <li className="flex gap-2"><span className="text-[#00aef7] font-bold">1.</span> User signs up and connects social accounts via OAuth</li>
                <li className="flex gap-2"><span className="text-[#00aef7] font-bold">2.</span> We request only needed permissions: page publishing, media upload, engagement metrics</li>
                <li className="flex gap-2"><span className="text-[#00aef7] font-bold">3.</span> User creates or selects content in our Design Center</li>
                <li className="flex gap-2"><span className="text-[#00aef7] font-bold">4.</span> We publish to their connected accounts using official platform APIs</li>
                <li className="flex gap-2"><span className="text-[#00aef7] font-bold">5.</span> We fetch engagement data (likes, comments, shares, views) to show performance</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Platform & Security</h3>
              <ul className="space-y-1.5 text-xs text-gray-600">
                {[
                  'OAuth 2.0 authentication — we never store passwords',
                  'SSL/TLS encryption on all connections',
                  'Users control which accounts are connected',
                  'Data deletion available on request',
                  'Hosted on AWS with automated backups',
                  'Real-time Matrix messaging (end-to-end encrypted)',
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0"><path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" /></svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>


      {/* ══════════ CTA ══════════ */}
      <section className="bg-gray-900">
        <div className="mx-auto w-[90%] max-w-4xl py-10 md:py-14 text-center">
          <h2 className="text-xl md:text-2xl font-bold text-white leading-tight">
            Stop struggling with marketing.<br />Start growing your business.
          </h2>
          <p className="mt-3 text-gray-400 text-sm max-w-xl mx-auto">
            Join other local professionals who use eHandshake to create professional content, publish to every platform, and build their reputation &mdash; Collectively &mdash; without the headache.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <a
              href="/?modal=signup"
              className="inline-flex items-center px-6 py-3 rounded-lg bg-[#00aef7] text-white text-sm font-semibold hover:bg-[#0098d8] transition-colors shadow-lg shadow-[#00aef7]/25"
            >
              Get Started Free
            </a>
            <a
              href={`${PUBLIC_SITE}/search`}
              className="inline-flex items-center px-6 py-3 rounded-lg border border-gray-600 text-gray-300 text-sm font-semibold hover:bg-gray-800 transition-colors"
            >
              Browse Directory
            </a>
          </div>
        </div>
      </section>

    </main>
  );
};

export default Home;
